import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Save, Star, Eraser } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "../lib/api";
import { toPreviewLink } from "../lib/drive";
import { getColor } from "../lib/categoryColors";
import { useAuth } from "../context/AuthContext";
import { useCategories } from "../context/CategoriesContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { CarrdEmbedDialog } from "../components/CarrdEmbedDialog";
import { AdminNav } from "../components/AdminNav";
import { AnalyticsTile } from "../components/AnalyticsTile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";

const EMPTY_FORM = {
  title: "",
  description: "",
  folder: "",
  googleDriveLink: "",
  featured: false,
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { categories } = useCategories();
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formError, setFormError] = useState("");
  const [wipeConfirm, setWipeConfirm] = useState(false);

  const loadPdfs = async () => {
    try {
      const { data } = await http.get("/pdfs");
      setPdfs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load PDFs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPdfs();
  }, []);

  const previewComputed = useMemo(() => toPreviewLink(form.googleDriveLink), [form.googleDriveLink]);
  const hasSamples = useMemo(
    () => pdfs.some((p) => /\/file\/d\/1FAKE/.test(p.googleDriveLink || "")),
    [pdfs]
  );

  if (user === null) return null; // still checking
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, folder: categories[0]?.name || "" });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (pdf) => {
    setEditing(pdf);
    setForm({
      title: pdf.title,
      description: pdf.description || "",
      folder: pdf.folder,
      googleDriveLink: pdf.googleDriveLink,
      featured: !!pdf.featured,
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.title.trim() || !form.googleDriveLink.trim() || !form.folder) {
      setFormError("Title, category and Google Drive link are required.");
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await http.put(`/pdfs/${editing.id}`, form);
        toast.success("PDF updated");
      } else {
        await http.post("/pdfs", form);
        toast.success("PDF added");
      }
      setDialogOpen(false);
      await loadPdfs();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await http.delete(`/pdfs/${deleteTarget.id}`);
      toast.success("PDF deleted");
      setDeleteTarget(null);
      await loadPdfs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const handleWipeSamples = async () => {
    // Delete any PDF whose googleDriveLink points to one of the seeded fake IDs (starts with "1FAKE")
    const samples = pdfs.filter((p) => /\/file\/d\/1FAKE/.test(p.googleDriveLink || ""));
    if (samples.length === 0) {
      toast.info("No sample PDFs to clear");
      setWipeConfirm(false);
      return;
    }
    try {
      await Promise.all(samples.map((p) => http.delete(`/pdfs/${p.id}`)));
      toast.success(`Cleared ${samples.length} sample PDF${samples.length === 1 ? "" : "s"}`);
      setWipeConfirm(false);
      await loadPdfs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to clear samples");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <AdminNav />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            Manage PDFs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Add, edit or remove PDFs. Google Drive share links are converted into preview links automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasSamples && (
            <Button
              variant="outline"
              onClick={() => setWipeConfirm(true)}
              className="h-11 border-slate-300 text-slate-600 hover:text-rose-700 hover:border-rose-200 rounded-lg"
              data-testid="admin-wipe-samples-btn"
            >
              <Eraser className="h-4 w-4" />
              Clear sample PDFs
            </Button>
          )}
          <CarrdEmbedDialog />
          <Button
            onClick={openAdd}
            className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg h-11"
            data-testid="admin-add-pdf-btn"
          >
            <Plus className="h-4 w-4" />
            Add PDF
          </Button>
        </div>
      </div>

      <AnalyticsTile onPinned={loadPdfs} />

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-white border border-slate-200 animate-pulse" />
          ))}
        </div>
      ) : pdfs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center">
          <h3 className="font-display text-xl font-bold text-slate-900">No PDFs yet</h3>
          <p className="text-sm text-slate-500 mt-1">Add your first PDF to start building your library.</p>
          <Button
            onClick={openAdd}
            className="mt-5 bg-brand-primary hover:bg-brand-primary-hover text-white"
            data-testid="admin-empty-add-btn"
          >
            <Plus className="h-4 w-4" /> Add PDF
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-pdf-table">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
            <div className="col-span-6">Title</div>
            <div className="col-span-3">Category</div>
            <div className="col-span-1">Featured</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <ul className="divide-y divide-slate-200">
            {pdfs.map((pdf) => {
              const color = getColor(categories.find((c) => c.name === pdf.folder)?.color);
              return (
                <li
                  key={pdf.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-4 items-center hover:bg-slate-50/60 transition-colors"
                  data-testid={`admin-pdf-row-${pdf.id}`}
                >
                  <div className="md:col-span-6 min-w-0">
                    <div className="font-display font-bold text-slate-900 truncate">{pdf.title}</div>
                    {pdf.description && (
                      <div className="text-sm text-slate-500 line-clamp-1 mt-0.5">{pdf.description}</div>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <Badge
                      className={cn("rounded-full border-transparent font-semibold", color.bg, color.text)}
                      style={color.badgeStyle}
                    >
                      <span
                        className={cn("h-1.5 w-1.5 rounded-full mr-1.5", color.dot)}
                        style={color.dotStyle}
                      />
                      {pdf.folder}
                    </Badge>
                  </div>
                  <div className="md:col-span-1">
                    {pdf.featured ? (
                      <Badge className="rounded-full bg-brand-accent text-brand-primary border-transparent">
                        <Star className="h-3 w-3 mr-1 fill-current" /> Yes
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">No</span>
                    )}
                  </div>
                  <div className="md:col-span-2 flex md:justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(pdf)}
                      data-testid={`admin-edit-${pdf.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(pdf)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      data-testid={`admin-delete-${pdf.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold">
              {editing ? "Edit PDF" : "Add a new PDF"}
            </DialogTitle>
            <DialogDescription>
              Paste a Google Drive share link — we'll generate the preview link automatically.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-pdf-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Soccer Practice Plan — U10 Dribbling"
                required
                data-testid="admin-form-title"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary of what's inside this PDF"
                rows={3}
                data-testid="admin-form-description"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Category</Label>
                <Select value={form.folder} onValueChange={(val) => setForm({ ...form, folder: val })}>
                  <SelectTrigger data-testid="admin-form-folder">
                    <SelectValue placeholder="Pick a sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Featured</Label>
                <div className="h-9 flex items-center gap-3 px-3 rounded-md border border-slate-200">
                  <Switch
                    checked={form.featured}
                    onCheckedChange={(v) => setForm({ ...form, featured: v })}
                    data-testid="admin-form-featured"
                  />
                  <span className="text-sm text-slate-600">Show in Featured section</span>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Google Drive link</Label>
              <Input
                value={form.googleDriveLink}
                onChange={(e) => setForm({ ...form, googleDriveLink: e.target.value })}
                placeholder="https://drive.google.com/file/d/FILEID/view?usp=sharing"
                required
                data-testid="admin-form-drive-link"
              />
              {form.googleDriveLink && (
                <div className="text-xs text-slate-500 break-all">
                  <span className="font-bold text-slate-600">Preview:</span> {previewComputed}
                </div>
              )}
            </div>

            {formError && (
              <div className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2" data-testid="admin-form-error">
                {formError}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-brand-accent hover:bg-brand-accent-hover text-brand-primary font-bold"
                data-testid="admin-form-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editing ? "Save changes" : "Add PDF"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.title ? `"${deleteTarget.title}" ` : "This item "}
              will be permanently removed from your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="admin-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Wipe samples confirm */}
      <AlertDialog open={wipeConfirm} onOpenChange={setWipeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear the seeded sample PDFs?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every PDF that still uses the placeholder Google Drive IDs (the ones starting with
              <span className="font-mono"> 1FAKE</span>). Your real PDFs will not be touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-wipe-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleWipeSamples}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="admin-wipe-confirm"
            >
              Clear samples
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
